import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { MailboxWarning } from '../../components/MailboxWarning'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { DescriptorCard } from './DescriptorCard'
import { Glance } from './Glance'
import { HistoryPanel } from './HistoryPanel'
import { HowItWorks } from './HowItWorks'
import { ScenarioPane } from './ScenarioPane'
import { ScenarioRail } from './ScenarioRail'
import { TryIt } from './TryIt'

// The Voice Profile: the person's voice at a glance, then each scenario with its measured
// descriptor and their own messages as the evidence, a workbench to draft behind the verifier,
// the science, and the history. Everything shown is measured from their sent mail; the prose is a
// model's reading of those measurements, and the person is the only authority allowed to edit it.
export function Voice() {
  const spoke = useSpoke()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [scenario, setScenario] = useState<string>('all')

  const profile = useQuery({ queryKey: ['voice-profile', spoke], queryFn: () => voiceApi.profile(spoke), retry: false })
  // every scenario with its count and who decided its labels — the rail lists them all, not the largest four
  const scenarios = useQuery({ queryKey: ['voice-scenarios', spoke], queryFn: () => voiceApi.scenarios(spoke), retry: false })
  const build = useMutation({
    mutationFn: () => voiceApi.build(spoke),
    onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`),
  })
  const scan = useMutation({
    mutationFn: () => voiceApi.scan(spoke, 2000),
    onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`),
  })

  const refusal = profile.error instanceof RefusedError ? profile.error.refusal : null
  const oauthError = params.get('error')
  const jobId = params.get('job')
  if (jobId) return <Navigate to={`/jobs/${encodeURIComponent(jobId)}`} replace />

  return (
    <section className="page voice">
      {/* The mailbox this profile was measured from has stopped opening: say so before any of the
          numbers below, which are the last successful read's and not today's (REQ-064-AC3). */}
      <MailboxWarning lastReadAt={profile.data?.builtAt ?? null} />
      {oauthError && <p className="error">{oauthError}</p>}
      {profile.isPending && <p className="muted">Loading…</p>}

      {refusal && (
        <>
          <p className="kicker">Voice of the rep</p>
          <h1>Your voice</h1>
          <div className="card">
            <div className="refusal">
              <strong>{refusal.reason === 'NotAnswerableFromData' ? 'Not built yet' : refusal.reason}</strong>
              <p>{refusal.message}</p>
            </div>
            <div className="actions">
              {refusal.message.includes('mailbox') ? (
                <Link className="button primary" to="/mailbox">Connect a mailbox</Link>
              ) : (
                <button className="button primary" onClick={() => build.mutate()} disabled={build.isPending}>{build.isPending ? 'Starting…' : 'Read my mailbox'}</button>
              )}
              {build.isError && <span className="error">{build.error instanceof RefusedError ? build.error.refusal.message : build.error.message}</span>}
            </div>
          </div>
        </>
      )}
      {scan.isError && <p className="error">{scan.error instanceof RefusedError ? scan.error.refusal.message : scan.error.message}</p>}

      {profile.isSuccess && (() => {
        const all = [profile.data.overall, ...profile.data.scenarios]
        const current = all.find((s) => s.scenario === scenario)
        const counts = scenarios.data?.scenarios ?? []
        return (
          <>
            <header className="voice-head">
              <div>
                <p className="kicker">Voice of the rep · revision {profile.data.revision} · built {formatDate(profile.data.builtAt)}</p>
                <h1>Your voice</h1>
                <p className="lede">
                  Measured from {profile.data.messageCount.toLocaleString()} messages you sent from {profile.data.address}, across {profile.data.scenarios.length} scenarios.
                  {profile.data.provisional ? ' The numbers are real; no model has described them in words yet.' : ' Described by a model, checked against your own held-out mail.'}
                </p>
              </div>
              <div className="actions">
                <button className="button" onClick={() => build.mutate()} disabled={build.isPending}>{build.isPending ? 'Starting…' : 'Read new mail'}</button>
                {profile.data.modelConfigured && <button className="button" onClick={() => scan.mutate()} disabled={scan.isPending} title="A model reads your newest 2,000 messages and says who each was to and why; scenarios sharpen from 'clients and contacts' to clients, prospects, leadership, vendors. Each press sorts the next 2,000.">{scan.isPending ? 'Starting…' : 'Sort with a model'}</button>}
                <Link className="button" to="/mailbox">Mailbox</Link>
              </div>
            </header>

            <Glance profile={profile.data} onPick={setScenario} />

            <div className="voice-body">
              <ScenarioRail scenarios={all} current={scenario} onPick={setScenario} counts={counts} />
              <div className="voice-detail">
                {current && (
                  <div className="card">
                    <DescriptorCard scenario={current} overall={profile.data.overall} />
                  </div>
                )}
                <ScenarioPane scenarioKey={scenario} view={current} entry={counts.find((c) => c.scenario === scenario)} />
              </div>
            </div>

            <TryIt scenarios={profile.data.scenarios.map((s) => s.scenario)} modelConfigured={profile.data.modelConfigured} initialScenario={scenario} />
            <HowItWorks styleModel={profile.data.styleModel} styleDimensions={profile.data.styleDimensions} />
            <HistoryPanel />
          </>
        )
      })()}
    </section>
  )
}
